import type { Ref } from 'vue'
import { ref } from 'vue'

export const useTogglePositionFixed = (
  elementRef: Ref<HTMLElement | null>
): {
  measure: () => void
  setPositionFixed: () => void
  resetPosition: () => void
} => {
  const left = ref(0)
  const top = ref(0)
  const height = ref(0)
  const width = ref(0)
  const zIndex = ref('')
  const position = ref('')

  function measure(): void {
    const element = elementRef.value

    if (!element || element.style.position === 'fixed') {
      return
    }

    const parent = element.parentElement

    if (!parent) {
      return
    }

    const box = element.getBoundingClientRect()

    lockParentDimensions({
      height: box.height,
      width: box.width,
    })

    storeInitialAttributes({
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
      position: element.style.position,
      zIndex: element.style.zIndex,
    })
  }

  function storeInitialAttributes({
    left: leftValue,
    top: topValue,
    width: widthValue,
    height: heightValue,
    position: positionValue,
    zIndex: zIndexValue,
  }: {
    left: number
    top: number
    width: number
    height: number
    position: string
    zIndex: string
  }): void {
    left.value = leftValue
    top.value = topValue
    width.value = widthValue
    height.value = heightValue
    position.value = positionValue
    zIndex.value = zIndexValue
  }

  // This function locks the parent element's dimensions to prevent it from collapsing
  function lockParentDimensions({
    height: heightValue,
    width: widthValue,
  }: {
    height: number
    width: number
  }): void {
    const element = elementRef.value
    const parent = element?.parentElement

    if (!element || !parent) {
      return
    }

    parent.style.height = `${heightValue}px`
    parent.style.width = `${widthValue}px`
  }

  function setPositionFixed(): void {
    const element = elementRef.value

    if (!element || element.style.position === 'fixed') {
      return
    }

    element.style.position = 'fixed'
    element.style.left = `${left.value}px`
    element.style.width = width.value + 'px'
    element.style.top = `${top.value}px`
    element.style.zIndex = '99999'
  }

  function resetPosition(): void {
    const element = elementRef.value

    if (!element || element.style.position !== 'fixed') {
      return
    }

    element.style.position = position.value
    element.style.left = 'auto'
    element.style.width = 'auto'
    element.style.top = 'auto'
    element.style.zIndex = zIndex.value ? zIndex.value.toString() : 'auto'
  }

  return {
    measure,
    setPositionFixed,
    resetPosition,
  }
}
